
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */



const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h2>{`Server Side Rendering | v2+`}</h2>
    <p>{`styled-components supports concurrent server side rendering, with stylesheet rehydration.
The basic idea is that everytime you render your app on the server, you can create
a `}<inlineCode parentName="p">{`ServerStyleSheet`}</inlineCode>{` and add a provider to your React tree, that accepts styles
via a context API.`}</p>
    <p>{`This doesn't interfere with global styles, such as `}<inlineCode parentName="p">{`keyframes`}</inlineCode>{` or `}<inlineCode parentName="p">{`createGlobalStyle`}</inlineCode>{` and
allows you to use styled-components with React DOM's various SSR APIs.`}</p>
    <h3>{`Tooling setup`}</h3>
    <p>{`In order to reliably perform server side rendering and have the client side bundle pick up without issues, you'll need to use our `}<a parentName="p" {...{
        "href": "/docs/tooling#babel-plugin"
      }}>{`babel plugin`}</a>{`. It prevents checksum mismatches by adding a deterministic ID to each styled component. Refer to the `}<a parentName="p" {...{
        "href": "/docs/tooling#serverside-rendering"
      }}>{`tooling documentation`}</a>{` for more information.`}</p>
    <p>{`For TypeScript users, our resident TS guru Igor Oleinikov put together a `}<a parentName="p" {...{
        "href": "/docs/tooling#typescript-plugin"
      }}>{`TypeScript plugin`}</a>{` for the webpack ts-loader / awesome-typescript-loader toolchain that accomplishes some similar tasks.`}</p>
    <p>{`If possible, we definitely recommend using the babel plugin though because it is updated the most frequently. It's now possible to `}<a parentName="p" {...{
        "href": "https://babeljs.io/docs/en/babel-preset-typescript"
      }}>{`compile TypeScript using Babel`}</a>{`, so it may be worth switching off TS loader and onto a pure Babel implementation to reap the ecosystem benefits.`}</p>
    <h3>{`Example`}</h3>
    <p>{`The basic API goes as follows:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { renderToString } from 'react-dom/server';
import { ServerStyleSheet } from 'styled-components';

const sheet = new ServerStyleSheet();
try {
  const html = renderToString(sheet.collectStyles(<YourApp />));
  const styleTags = sheet.getStyleTags(); // or sheet.getStyleElement();
} catch (error) {
  // handle error
  console.error(error);
} finally {
  sheet.seal();
}
`}</code></pre>
    <p>{`The `}<inlineCode parentName="p">{`collectStyles`}</inlineCode>{` method wraps your element in a provider. Optionally you can use
the `}<inlineCode parentName="p">{`StyleSheetManager`}</inlineCode>{` provider directly, instead of this method. Just make sure not to
use it on the client-side.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { renderToString } from 'react-dom/server';
import { ServerStyleSheet, StyleSheetManager } from 'styled-components';

const sheet = new ServerStyleSheet();
try {
  const html = renderToString(
    <StyleSheetManager sheet={sheet.instance}>
      <YourApp />
    </StyleSheetManager>
  );
  const styleTags = sheet.getStyleTags(); // or sheet.getStyleElement();
} catch (error) {
  // handle error
  console.error(error);
} finally {
  sheet.seal();
}
`}</code></pre>
    <p>{`The `}<inlineCode parentName="p">{`sheet.getStyleTags()`}</inlineCode>{` returns a string of multiple `}<inlineCode parentName="p">{`<style>`}</inlineCode>{` tags.
You need to take this into account when adding the CSS string to your HTML output.`}</p>
    <p>{`Alternatively the `}<inlineCode parentName="p">{`ServerStyleSheet`}</inlineCode>{` instance also has a `}<inlineCode parentName="p">{`getStyleElement()`}</inlineCode>{` method
that returns an array of React elements.`}</p>
    <p>{`If rendering fails for any reason it's a good idea to use `}<inlineCode parentName="p">{`try...catch...finally`}</inlineCode>{` to ensure that the `}<inlineCode parentName="p">{`sheet`}</inlineCode>{` object will always be available for garbage collection. Make sure `}<inlineCode parentName="p">{`sheet.seal()`}</inlineCode>{` is only called after `}<inlineCode parentName="p">{`sheet.getStyleTags()`}</inlineCode>{` or `}<inlineCode parentName="p">{`sheet.getStyleElement()`}</inlineCode>{` have been called otherwise a different error will be thrown.`}</p>
    <blockquote>
      <p parentName="blockquote"><inlineCode parentName="p">{`sheet.getStyleTags()`}</inlineCode>{` and `}<inlineCode parentName="p">{`sheet.getStyleElement()`}</inlineCode>{` can only be called after your element is rendered. As a result, components from `}<inlineCode parentName="p">{`sheet.getStyleElement()`}</inlineCode>{` cannot be combined with `}<inlineCode parentName="p">{`<YourApp />`}</inlineCode>{` into a larger component.`}</p>
    </blockquote>
    <h3>{`Next.js`}</h3>
    <h4>{`With Babel`}</h4>
    <p>{`Basically you need to add a custom `}<inlineCode parentName="p">{`pages/_document.js`}</inlineCode>{` (if you don't have one). Then `}<a parentName="p" {...{
        "href": "https://github.com/vercel/next.js/blob/canary/examples/with-styled-components-babel/pages/_document.tsx"
      }}>{`copy the logic`}</a>{` for styled-components to inject the server side rendered styles into the `}<inlineCode parentName="p">{`<head>`}</inlineCode>{`.`}</p>
    <p>{`Refer to `}<a parentName="p" {...{
        "href": "https://github.com/vercel/next.js/tree/canary/examples/with-styled-components-babel"
      }}>{`our example`}</a>{` in the Next.js repo for an up-to-date usage example.`}</p>
    <h4>{`With SWC`}</h4>
    <p><a parentName="p" {...{
        "href": "https://nextjs.org/blog/next-12"
      }}>{`Since version 12`}</a>{`, Next.js uses a Rust compiler called SWC. If you're not using any babel plugin, you should refer to `}<a parentName="p" {...{
        "href": "https://github.com/vercel/next.js/tree/canary/examples/with-styled-components"
      }}>{`this example`}</a>{` instead.`}</p>
    <p>{`On this version, you `}<a parentName="p" {...{
        "href": "https://github.com/vercel/next.js/blob/canary/examples/with-styled-components/next.config.js"
      }}>{`only need to add`}</a>{` `}<inlineCode parentName="p">{`styledComponents: true,`}</inlineCode>{` at the compiler options in the `}<inlineCode parentName="p">{`next.config.js`}</inlineCode>{` file and modify `}<inlineCode parentName="p">{`_document`}</inlineCode>{` file with `}<inlineCode parentName="p">{`getInitialProps`}</inlineCode>{` as in this `}<a parentName="p" {...{
        "href": "https://github.com/vercel/next.js/blob/canary/examples/with-styled-components/pages/_document.tsx"
      }}>{`example`}</a>{` to support SSR.`}</p>
    <h3>{`Gatsby`}</h3>
    <p><a parentName="p" {...{
        "href": "https://www.gatsbyjs.org/"
      }}>{`Gatsby`}</a>{` has an official plugin that enables server-side rendering for styled-components.`}</p>
    <p>{`Refer to `}<a parentName="p" {...{
        "href": "https://www.gatsbyjs.org/packages/gatsby-plugin-styled-components/"
      }}>{`the plugin's page`}</a>{` for setup and usage instructions.`}</p>
    <h3>{`Streaming Rendering`}</h3>
    <p>{`styled-components offers a streaming API for use with `}<a parentName="p" {...{
        "href": "https://reactjs.org/docs/react-dom-server.html#rendertonodestream"
      }}>{`ReactDOMServer.renderToNodeStream()`}</a>{`. There are two parts to a streaming implementation:`}</p>
    <p><em parentName="p">{`On the server:`}</em></p>
    <p><inlineCode parentName="p">{`ReactDOMServer.renderToNodeStream`}</inlineCode>{` emits a "readable" stream that styled-components wraps. As whole chunks of HTML are pushed onto the stream, if any corresponding styles are ready to be rendered, a style block is prepended to React's HTML and forwarded on to the client browser.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { renderToNodeStream } from 'react-dom/server';
import styled, { ServerStyleSheet } from 'styled-components';

// if you're using express.js, you'd have access to the response object "res"

// typically you'd want to write some preliminary HTML, since React doesn't handle this
res.write('<html><head><title>Test</title></head><body>');

const Heading = styled.h1\`
  color: red;
\`;

const sheet = new ServerStyleSheet();
const jsx = sheet.collectStyles(<Heading>Hello SSR!</Heading>);
const stream = sheet.interleaveWithNodeStream(renderToNodeStream(jsx));

// you'd then pipe the stream into the response object until it's done
stream.pipe(res, { end: false });

// and finalize the response with closing HTML
stream.on('end', () => res.end('</body></html>'));
`}</code></pre>
    <p><em parentName="p">{`On the client:`}</em></p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { hydrate } from 'react-dom';

hydrate();
// your client-side react implementation
`}</code></pre>
    <p>{`After client-side rehydration is complete, styled-components will take over as usual and inject any further dynamic styles after the relocated streaming ones.`}</p>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;